/**
 * expandSelectStar — replaces a bare `*` SELECT list with the explicit column
 * list of the tables in scope, so the column chooser (remove ×, reorder, add)
 * has real chips to work on. `SELECT *` is a single select item to SR-Edit,
 * so there is no structural op for "explode the star"; like
 * `applyMergeConvert` this is a thin range-replace applier over the star's
 * span, applied under the shared `vqe-edit` undo source and followed by the
 * same resolver re-sync + commit-feedback record every structural commit
 * makes (so "Run on change" and the once-per-session teaching toast fire).
 *
 * Stale-buffer guard mirrors `commitEdit`: if the live buffer moved since the
 * star was rendered, the span no longer lines up — refuse instead of splicing.
 */
import { useVqeStore } from '../../store/vqeStore';
import { VQE_UNDO_SOURCE, rangeFromOffsets } from '../commitEdit';
import { extractStructureForContext, statementSpanOf, type VqeEditContext } from '../editContext';
import { starColumns, type StarExpansion } from '../../../../sql-resolver/scope/starColumns';

/**
 * What `*` expands to in the context statement: its own tables' columns in
 * the order and number the engine's `*` returns them, a USING / NATURAL join's
 * shared columns once (`starColumns`). Never an enclosing query's tables.
 */
export function starExpansionFor(editContext: VqeEditContext): StarExpansion {
  const span = statementSpanOf(editContext);
  try {
    if (span) return starColumns(extractStructureForContext(editContext), span.options, editContext.service.getSchemaSnapshot());
  } catch {
    // An unparseable statement expands to nothing.
  }
  return { kind: 'unknown' };
}

/** Bare `*` only — a qualified `t.*` is left to the user (it names one table on purpose). */
export function isBareStar(expression: string): boolean {
  return expression.trim() === '*';
}

export interface ExpandSelectStarParams {
  readonly editContext: VqeEditContext;
  /** Absolute offsets of the `*` item inside `originalSource`. */
  readonly starSpan: { readonly start: number; readonly end: number };
  /** The buffer the star was rendered against. */
  readonly originalSource: string;
  /** The select items replacing `*` (`starExpansionFor`). */
  readonly items: readonly string[];
}

export async function expandSelectStar({
  editContext,
  starSpan,
  originalSource,
  items,
}: ExpandSelectStarParams): Promise<boolean> {
  if (items.length === 0) return false;
  const { model, editor, service, documentId, getLiveSource } = editContext;
  if (getLiveSource && getLiveSource() !== originalSource) {
    useVqeStore.getState().recordCommitFeedback({
      kind: 'failure',
      at: Date.now(),
      message: 'The buffer changed before the columns could be expanded — try again',
    });
    return false;
  }
  // Trim the span to the `*` itself so surrounding whitespace survives.
  const raw = originalSource.slice(starSpan.start, starSpan.end);
  const leading = raw.length - raw.trimStart().length;
  const trailing = raw.length - raw.trimEnd().length;
  const start = starSpan.start + leading;
  const end = starSpan.end - trailing;
  const text = items.join(', ');
  const emitted = originalSource.slice(0, start) + text + originalSource.slice(end);

  const edit = { range: rangeFromOffsets(model, start, end), text };
  if (editor) editor.executeEdits(VQE_UNDO_SOURCE, [edit]);
  else model.pushEditOperations(null, [edit], () => null);
  await service.onTextChange(documentId, emitted);
  useVqeStore.getState().recordCommitFeedback({ kind: 'success', at: Date.now(), documentId });
  return true;
}

